/**
 * assets/linked-products.js */

(function () {
  'use strict';

  /* ── Configuration ─────────────────────────────────────────────────────── */

  const SECTION_ID_ATTR   = 'data-section-id';
  const LOADING_CLASS     = 'linked-products-loading--active';
  const SWATCH_CLASS      = 'linked-product-swatch';
  const CURRENT_CLASS     = 'is-current';
  const UNAVAIL_CLASS     = 'is-unavailable';

  /* Cache of already-fetched section HTML: handle → HTML string */
  const _cache = {};

  /* ── Utility: wait for a custom element to be defined ──────────────────── */
  function whenDefined(tagName) {
    return customElements.whenDefined(tagName).catch(() => Promise.resolve());
  }

  /* ── Find the closest product section wrapper ───────────────────────────── */
  function getSectionId(swatchEl) {
    const picker = swatchEl.closest('.linked-products-picker');
    return picker ? picker.getAttribute(SECTION_ID_ATTR) : null;
  }

  /* ── Toggle loading overlay ─────────────────────────────────────────────── */
  function setLoading(sectionId, on) {
    const picker = document.querySelector(
      `.linked-products-picker[${SECTION_ID_ATTR}="${sectionId}"]`
    );
    if (!picker) return;
    const overlay = picker.querySelector('.linked-products-loading');
    if (!overlay) return;
    overlay.classList.toggle('linked-products-loading--active', on);
    // Also dim the product-info column
    const infoCol = document.querySelector('.product-info-content');
    if (infoCol) infoCol.classList.toggle('linked-products-updating', on);
  }

  /* ── Fetch section HTML for a product handle ────────────────────────────── */
  async function fetchProductSection(productUrl, sectionId) {
    const cacheKey = `${productUrl}__${sectionId}`;
    if (_cache[cacheKey]) return _cache[cacheKey];

    const url = `${productUrl}?sections=${sectionId}`;
    const resp = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!resp.ok) throw new Error(`[linked-products] fetch failed: ${resp.status}`);

    const json = await resp.json();
    const html = json[sectionId];
    if (!html) throw new Error(`[linked-products] section "${sectionId}" not in response`);

    _cache[cacheKey] = html;
    return html;
  }

  /* ── Parse an HTML string into a Document ──────────────────────────────── */
  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  /* ── Replace a DOM region by ID from parsed doc ─────────────────────────── */
  function swapById(liveDoc, incomingDoc, id) {
    const live     = liveDoc.getElementById(id);
    const incoming = incomingDoc.getElementById(id);
    if (live && incoming) {
      live.innerHTML = incoming.innerHTML;
      // Copy relevant attributes (aria, data, class)
      Array.from(incoming.attributes).forEach(attr => {
        if (attr.name !== 'id') live.setAttribute(attr.name, attr.value);
      });
      return true;
    }
    return false;
  }

  /* ── Replace a DOM region by query selector ─────────────────────────────── */
  function swapByQuery(liveDoc, incomingDoc, selector) {
    const live     = liveDoc.querySelector(selector);
    const incoming = incomingDoc.querySelector(selector);
    if (live && incoming) {
      live.outerHTML = incoming.outerHTML;
      return true;
    }
    return false;
  }

  /* ── Re-initialise the Flickity media gallery ───────────────────────────── */
  function reinitGallery(sectionId) {
    const galleryEl = document.getElementById(`MediaGallery-${sectionId}`);
    if (!galleryEl) return;

    // Disconnecting and reconnecting forces its connectedCallback to re-run.
    const parent = galleryEl.parentNode;
    const next   = galleryEl.nextSibling;
    parent.removeChild(galleryEl);
    // Allow one microtask tick
    requestAnimationFrame(() => {
      parent.insertBefore(galleryEl, next);
    });
  }

  /* ── Re-initialise the native variant picker custom elements ─────────────── */
  function reinitVariantPickers(sectionId) {
    ['variant-radios', 'variant-selects'].forEach(tag => {
      const el = document.querySelector(`${tag}[data-section="${sectionId}"]`);
      if (!el) return;
      // Force the custom element lifecycle by upgrading it
      const replacement = el.cloneNode(true);
      el.replaceWith(replacement);
    });
  }

  /* ── Mark the clicked swatch as active, clear others ────────────────────── */
  function updateSwatchStates(picker, clickedHandle) {
    picker.querySelectorAll(`.${SWATCH_CLASS}`).forEach(swatch => {
      const isThis = swatch.dataset.handle === clickedHandle;
      swatch.classList.toggle(CURRENT_CLASS, isThis);
      swatch.setAttribute('aria-current', isThis ? 'true' : 'false');
    });
  }

  /* ── Preserve custom product-option field values across swaps ───────────── */
  function captureCustomOptions(formId) {
    const form = document.getElementById(formId);
    if (!form) return null;
    const values = {};
    form.querySelectorAll('[name^="properties["]').forEach(el => {
      values[el.name] = el.type === 'file' ? null : el.value;
    });
    return values;
  }

  function restoreCustomOptions(formId, values) {
    if (!values) return;
    const form = document.getElementById(formId);
    if (!form) return;
    form.querySelectorAll('[name^="properties["]').forEach(el => {
      if (el.name in values && values[el.name] !== null) {
        el.value = values[el.name];
      }
    });
  }

  /* ── Main swap handler ──────────────────────────────────────────────────── */
  async function loadLinkedProduct(productUrl, productHandle, sectionId) {
    setLoading(sectionId, true);

    const formId        = `product-form-${sectionId}`;
    const savedOptions  = captureCustomOptions(formId);

    try {
      const html        = await fetchProductSection(productUrl, sectionId);
      const incomingDoc = parseHTML(html);
      const liveDoc     = document;

      /* ── 1. Gallery ─────────────────────────────────────────────────────── */
      const liveGallery     = liveDoc.getElementById(`MediaGallery-${sectionId}`);
      const incomingGallery = incomingDoc.getElementById(`MediaGallery-${sectionId}`);
      if (liveGallery && incomingGallery) {
        liveGallery.outerHTML = incomingGallery.outerHTML;
      }

      /* ── 2. Product title (h1) ──────────────────────────────────────────── */
      const liveH1     = liveDoc.querySelector('.product-info-heading');
      const incomingH1 = incomingDoc.querySelector('.product-info-heading');
      if (liveH1 && incomingH1) {
        liveH1.innerHTML = incomingH1.innerHTML;
      }

      /* ── 3. Price block ─────────────────────────────────────────────────── */
      swapById(liveDoc, incomingDoc, `price-${sectionId}`);

      /* ── 4. Variant picker ──────────────────────────────────────────────── */
      const livePicker     = liveDoc.querySelector('.product-block-variants');
      const incomingPicker = incomingDoc.querySelector('.product-block-variants');
      if (livePicker && incomingPicker) {
        livePicker.outerHTML = incomingPicker.outerHTML;
      }

      /* ── 5. Add-to-cart form (product-form custom element) ──────────────── */
      const liveForm     = liveDoc.querySelector('product-form');
      const incomingForm = incomingDoc.querySelector('product-form');
      if (liveForm && incomingForm) {
        liveForm.outerHTML = incomingForm.outerHTML;
        restoreCustomOptions(formId, savedOptions);
      }

      /* ── 6. Linked-products picker (re-render swatch row) ───────────────── */
      const liveLPPicker     = liveDoc.querySelector(`.linked-products-picker[${SECTION_ID_ATTR}="${sectionId}"]`);
      const incomingLPPicker = incomingDoc.querySelector(`.linked-products-picker[${SECTION_ID_ATTR}="${sectionId}"]`);
      if (liveLPPicker && incomingLPPicker) {
        liveLPPicker.outerHTML = incomingLPPicker.outerHTML;
      }

      /* ── 7. Page title & meta ───────────────────────────────────────────── */
      const incomingTitle = incomingDoc.querySelector('title');
      if (incomingTitle) document.title = incomingTitle.textContent;

      /* ── 8. URL ─────────────────────────────────────────────────────────── */
      history.replaceState({ linkedProductHandle: productHandle }, '', productUrl);

      /* ── 9. Re-initialise JS ────────────────────────────────────────────── */
      reinitGallery(sectionId);
      reinitVariantPickers(sectionId);

      // Dispatch custom event for any third-party integrations listening
      document.dispatchEvent(
        new CustomEvent('linked-product:changed', {
          bubbles: true,
          detail: { handle: productHandle, url: productUrl, sectionId },
        })
      );

    } catch (err) {
      console.error('[linked-products]', err);
      // Fallback: navigate normally so the customer still reaches the product
      window.location.href = productUrl;
    } finally {
      setLoading(sectionId, false);
    }
  }

  /* ── Event delegation: click on any swatch link ─────────────────────────── */
  document.addEventListener('click', function (event) {
    const swatch = event.target.closest(`.${SWATCH_CLASS}`);
    if (!swatch) return;

    // The current-product swatch is a <span>, not an <a> — no action needed
    if (swatch.classList.contains(CURRENT_CLASS)) return;

    // If it's a link (sibling) – intercept and use AJAX instead
    if (swatch.tagName === 'A') {
      event.preventDefault();
    }

    const productUrl    = swatch.dataset.url;
    const productHandle = swatch.dataset.handle;
    const sectionId     = getSectionId(swatch);

    if (!productUrl || !productHandle || !sectionId) return;

    // Optimistically mark this swatch as active
    const picker = swatch.closest('.linked-products-picker');
    if (picker) updateSwatchStates(picker, productHandle);

    // loadLinkedProduct(productUrl, productHandle, sectionId); // to load only product section
    window.location.href = productUrl; // to load fulll page

  });

  /* ── Tooltip: show on hover (mirrors theme tooltip behaviour) ────────────── */
  document.addEventListener('mouseover', function (event) {
    const swatch = event.target.closest(`.${SWATCH_CLASS}`);
    if (!swatch || !swatch.dataset.tooltip) return;
    swatch.setAttribute('title', swatch.dataset.tooltip);
  });

})();
